import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Link,
} from '@mui/material';
import { CheckCircle, Email, ArrowBack, PrivacyTip, Extension } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { trackButtonClick } from '../utils/analytics';

interface HelpPageProps {
  onBack: () => void;
}

const HelpPage: React.FC<HelpPageProps> = ({ onBack }) => {
  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            trackButtonClick('Back', 'Help Page');
            onBack();
          }}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1">
          Help & Information
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          What is Bufferio?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Bufferio is a clipboard sharing service that lets you seamlessly transfer text, images, and files between your devices using Web and Chrome Extension.
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
          How it works:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Send to your devices"
              secondary="Instantly share content to any of your registered devices"
              primaryTypographyProps={{ variant: 'body1', sx: { fontWeight: 500 } }}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Send to anyone"
              secondary="Generate a 6-digit code to share content with non-registered devices"
              primaryTypographyProps={{ variant: 'body1', sx: { fontWeight: 500 } }}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Multiple formats"
              secondary="Share text, images, and files up to 100MB"
              primaryTypographyProps={{ variant: 'body1', sx: { fontWeight: 500 } }}
            />
          </ListItem>
        </List>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<Extension />}
            component={Link}
            href="https://chromewebstore.google.com/detail/fpoefldafmjaalhaegehkgjgabkgfalh?utm_source=item-share-cb"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              textTransform: 'none',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
            onClick={() => trackButtonClick('Chrome Extension', 'Help Page')}
          >
            Install Chrome Extension
          </Button>
          
          <Button
            variant="contained"
            fullWidth
            startIcon={<Email />}
            component={Link}
            href="mailto:support@bufferio.app"
            sx={{ 
              textTransform: 'none',
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' }
            }}
            onClick={() => trackButtonClick('Contact Support', 'Help Page')}
          >
            Contact Support
          </Button>
          
          <Button
            variant="outlined"
            fullWidth
            startIcon={<PrivacyTip />}
            component={RouterLink}
            to="/privacy"
            sx={{ 
              textTransform: 'none',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(33, 150, 243, 0.1)'
              }
            }}
            onClick={() => trackButtonClick('Privacy Policy', 'Help Page')}
          >
            Privacy Policy
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default HelpPage;

